/**
 * minimapMath — the dependency-free part of the minimap.
 *
 * A "view" maps world pixels to minimap pixels with one uniform scale and a
 * translation: screen = t + world * scale. The small always-on panel and the
 * full-screen M-key overlay are the same drawing code fed different views.
 *
 * Kept free of PIXI and the DOM so it runs in Node tests.
 */

export const MINIMAP_DEFAULTS = {
  panelSize: 200,      // small panel edge, screen px
  worldRadius: 1500,   // world px from the player to the small panel's edge
  shotMarkerMs: 2000,  // how long an enemy's muzzle marker lingers after their last shot
  toggleKey: 'm',
  // Screen px kept clear around the large map. Top clears the zone panel,
  // bottom clears the inventory/ability slots, which draw above the overlay.
  overlayInsets: { top: 110, right: 40, bottom: 130, left: 40 }
};

/**
 * Axis-aligned bounds of a storm shape as the server serialises it.
 * @param {{type?: string, bounds?: object, center?: {x:number,y:number}, radius?: number}|null} shape
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}|null}
 */
export function zoneBounds(shape) {
  if (!shape) return null;
  if (shape.type === 'rect' && shape.bounds) {
    const { minX, minY, maxX, maxY } = shape.bounds;
    return { minX, minY, maxX, maxY };
  }
  if (shape.center && typeof shape.radius === 'number') {
    const { x, y } = shape.center;
    const r = shape.radius;
    return { minX: x - r, minY: y - r, maxX: x + r, maxY: y + r };
  }
  return null;
}

/**
 * View for the small panel: the player sits at the panel centre and the panel
 * edge is `worldRadius` world px away. Never clamps to the map edge — the
 * boundary rectangle shows where the arena ends.
 */
export function smallMapView(cx, cy, panelSize, worldRadius) {
  const scale = panelSize / (2 * worldRadius);
  return {
    scale,
    tx: panelSize / 2 - cx * scale,
    ty: panelSize / 2 - cy * scale
  };
}

/**
 * View for the large overlay: whole arena fit inside the screen minus the
 * insets, letterboxed and centred within the remaining area. `insets` is a
 * uniform number or { top, right, bottom, left }. A zero-sized map yields
 * scale 0 (nothing drawn) rather than NaN.
 */
export function largeMapView(mapW, mapH, screenW, screenH, insets = 0) {
  const i = typeof insets === 'number'
    ? { top: insets, right: insets, bottom: insets, left: insets }
    : { top: 0, right: 0, bottom: 0, left: 0, ...insets };
  const availW = Math.max(0, screenW - i.left - i.right);
  const availH = Math.max(0, screenH - i.top - i.bottom);
  const scale = mapW > 0 && mapH > 0 ? Math.min(availW / mapW, availH / mapH) : 0;
  const drawW = mapW * scale;
  const drawH = mapH * scale;
  return {
    scale,
    tx: i.left + (availW - drawW) / 2,
    ty: i.top + (availH - drawH) / 2,
    drawW,
    drawH
  };
}

/** World point → minimap point for the given view. */
export function project(view, wx, wy) {
  return { x: view.tx + wx * view.scale, y: view.ty + wy * view.scale };
}

/** Flat [x0,y0,x1,y1,…] world outline → flat minimap outline (feeds Graphics.poly). */
export function scalePoints(view, pts) {
  const out = new Array(pts.length);
  for (let i = 0; i < pts.length; i += 2) {
    out[i] = view.tx + pts[i] * view.scale;
    out[i + 1] = view.ty + pts[i + 1] * view.scale;
  }
  return out;
}

/** 1 at the moment of the shot, fading linearly to 0 at the end of the window. */
export function shotMarkerAlpha(shotTime, nowMs, windowMs) {
  if (windowMs <= 0) return 0;
  const t = (nowMs - shotTime) / windowMs;
  if (t <= 0) return 1;
  if (t >= 1) return 0;
  return 1 - t;
}

/** True once the marker window has fully elapsed (boundary inclusive). */
export function isExpiredShot(shotTime, nowMs, windowMs) {
  return nowMs - shotTime >= windowMs;
}

/**
 * View-model for dirty.changed(): the static-objects layer redraws only when
 * the world changed, the map size changed, or the scale changed.
 */
export function objectsKey(version, mapW, mapH, scale) {
  return { v: version, mapW, mapH, scale };
}

/**
 * Which world objects the map background shows: static geometry and
 * firewalls. Loot containers are too small to read at map scale and ability
 * pickups come and go (and have no theme colour), so both are skipped.
 */
export function isMinimapObject(obj) {
  if (!obj) return false;
  if (obj.objectType === 'lootContainer') return false;
  if (obj.abilityId || obj.type === 'ability_pickup') return false;
  return true;
}

export const MARKER_COLORS = {
  local: 0x00ffff,            // the existing "you" highlight (Leaderboard)
  shot: 0xff3b3b,             // enemy muzzle marker
  teammateFallback: 0x2ecc71  // only if teamColor() has no answer in a team mode
};

/** Pre-match warm-up roam: room is waiting or counting down before IN_PROGRESS. */
export function isPregameWarmup(roomState) {
  const s = roomState?.state;
  return s === 'waiting' || s === 'countdown';
}

/**
 * Which dots to draw, in draw order (local player last so it stays on top).
 *
 * Rules: the local player always; during pre-game warm-up every other player in
 * `players` is a red dot (the server sends map-wide positions then); teammates
 * only when teams are in play, on the local team, alive, and present in
 * `players` (the server culls distant ones in-match); enemies NEVER from
 * position in-match — only a fading marker where they last fired. A teammate's
 * shot is not an enemy marker: their team is looked up in `players`, then
 * `scoreboardPlayers` (which survives culling).
 *
 * @param {object} state - gameState-shaped: { localId, players, teams, roomState, scoreboardPlayers, recentShots, teamColor }
 * @param {number} nowMs - performance.now() clock (recentShots timestamps)
 * @param {number} windowMs - shot marker lifetime
 * @returns {Array<{kind:string,x:number,y:number,color:number,radius:number,alpha:number}>}
 */
export function collectMarkers(state, nowMs, windowMs) {
  const out = [];
  const local = state.players?.get?.(state.localId);
  if (!local) return out;

  const pregame = isPregameWarmup(state.roomState);
  const teamPlay = !pregame && Array.isArray(state.teams) && state.teams.length > 0;
  const localTeam = local.team;
  const teamOf = (id) => {
    const p = state.players.get(id) || state.scoreboardPlayers?.get?.(id);
    return p && Number.isInteger(p.team) ? p.team : null;
  };
  const isAlly = (id) => teamPlay && teamOf(id) === localTeam;

  if (pregame) {
    for (const p of state.players.values()) {
      if (p.id === state.localId) continue;
      out.push({ kind: 'pregame', x: p.x, y: p.y, color: MARKER_COLORS.shot, radius: 3, alpha: 1 });
    }
  } else if (teamPlay) {
    for (const p of state.players.values()) {
      if (p.id === state.localId || p.team !== localTeam) continue;
      if (p.health && p.health.currentHealth <= 0) continue;
      const color = state.teamColor?.(p.team) ?? MARKER_COLORS.teammateFallback;
      out.push({ kind: 'teammate', x: p.x, y: p.y, color, radius: 3, alpha: 1 });
    }
  }

  if (state.recentShots) {
    for (const [id, shot] of state.recentShots) {
      if (id === state.localId || isAlly(id)) continue;
      const alpha = shotMarkerAlpha(shot.time, nowMs, windowMs);
      if (alpha <= 0) continue;
      out.push({ kind: 'shot', x: shot.x, y: shot.y, color: MARKER_COLORS.shot, radius: 4, alpha });
    }
  }

  out.push({ kind: 'local', x: local.x, y: local.y, color: MARKER_COLORS.local, radius: 4, alpha: 1 });
  return out;
}
